/**
 * OAuth Callback Page
 *
 * Handles the OAuth redirect from Casdoor.
 * Exchanges the authorization code for an access token
 * and redirects to the dashboard.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import authService from '../services/authService';

const CallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);
  const isProcessing = useRef(false); // Prevent duplicate calls from React Strict Mode

  const handleCallback = useCallback(async () => {
    // Guard against multiple invocations (React Strict Mode)
    if (isProcessing.current) {
      return;
    }
    isProcessing.current = true;
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle OAuth error response
    if (errorParam) {
      setStatus('error');
      setError(errorDescription || errorParam || 'Authentication failed');
      setTimeout(() => navigate('/login?error=oauth_error'), 3000);
      return;
    }

    // Validate authorization code
    if (!code) {
      setStatus('error');
      setError('No authorization code received');
      setTimeout(() => navigate('/login?error=no_code'), 3000);
      return;
    }

    try {
      // Exchange code for token via backend
      const response = await authService.casdoorSignin(code, state);

      if (response.access_token && response.user) {
        // Store authentication data
        authService.storeUserData(response.user, response.access_token);

        setStatus('success');

        // Small delay to show success state, then redirect
        setTimeout(() => {
          // Check if there's a saved redirect path
          const redirectPath = sessionStorage.getItem('auth_redirect') || '/dashboard';
          sessionStorage.removeItem('auth_redirect');
          navigate(redirectPath, { replace: true });
        }, 1000);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('SSO callback error:', err);
      setStatus('error');
      setError(err.response?.data?.error || err.message || 'Login failed');

      // Redirect back to login after showing error
      setTimeout(() => {
        navigate('/login?error=sso_failed', { replace: true });
      }, 3000);
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            {status === 'processing' && 'Signing you in...'}
            {status === 'success' && 'Login Successful!'}
            {status === 'error' && 'Login Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {status === 'processing' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-muted-foreground">
                Please wait while we complete your authentication...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="text-muted-foreground">
                Redirecting to dashboard...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-red-500" />
              <p className="text-red-600 dark:text-red-400 text-center">
                {error}
              </p>
              <p className="text-sm text-muted-foreground">
                Redirecting to login page...
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CallbackPage;
